import { actions } from "redux/reducers";
import { useDispatch } from "react-redux";
import { SentimentType } from "types/sentiment";
import { useEffect, useState } from "react";
import { Moment } from "moment";
import moment from "moment";
import ReactECharts from "echarts-for-react";
import "./daily-insights.scss";

function DailyInsights({ sentiment, loading, dateFrom, dateTo }: { sentiment: SentimentType[]; loading: boolean; dateFrom: Moment; dateTo: Moment }) {
  const dispatch = useDispatch();
  const [chartData, setChartData] = useState<any[]>([]);
  const [xAxis, setXAxis] = useState<string[]>([]);
  const legend = ["None", "Positive", "Negative", "Balanced"];
  const startDate = dateFrom;
  const endDate = dateTo;

  const onClick = async (params: any) => {
    var data = [{}];
    var sentimentId = params.seriesName === "Positive" ? 3 : params.seriesName === "Balanced" ? 5 : params.seriesName === "Negative" ? 4 : params.seriesName === "Neutral" ? 7 : 0;
    var days = moment(endDate).diff(moment(startDate), "days");

    if (days > 30) {
      var dates = params.name.split(" - ");
      var year = moment(dates[1]).year();
      var dateFrom = moment(dates[0] + " " + year);
      var dateTo = moment(dates[1]);
      data = sentiment.filter((x) => moment(x.DatePosted).isBetween(dateFrom, dateTo) && x.ClassificationId === sentimentId);
    } else {
      var date = moment(params.name).date();
      data = sentiment.filter((x) => moment(x.DatePosted).date() === date && x.ClassificationId === sentimentId);
    }

    dispatch(
      actions.clipTray.set({
        open: true,
        clips: data,
      })
    );
  };

  const getClassification = (classification: string) => {
    switch (classification) {
      case "Positive":
        return {
          name: "Positive",
          color: "#007f0e",
        };
      case "Negative":
        return {
          name: "Negative",
          color: "#c00000",
        };
      case "Balanced":
        return {
          name: "Balanced",
          color: "#477bb3",
        };
      case "None":
        return {
          name: "None",
          color: "#a0a0a0",
        };
      default:
        return {
          name: "",
          color: "",
        };
    }
  };

  useEffect(() => {
    let arr: any[] = [];
    let _xAxis: string[] = [];
    const days = dateTo.diff(dateFrom, "days");

    if (days >= 30 && days < 90) {
      for (let i = dateFrom.clone(); i.isSameOrBefore(dateTo); i.add(7, "days")) {
        let dtFrom = i;
        let dtTo = i.clone().add(6, "d");
        if (dtTo > moment()) {
          dtTo = moment();
        }
        _xAxis.push(`${dtFrom.format("D MMM YYYY")} - ${dtTo.format("D MMM YYYY")}`);
        setXAxis(_xAxis);
      }
      for (let i = 0; i < legend.length; i++) {
        arr.push([]);
        for (let j = dateFrom.clone(); j.isSameOrBefore(dateTo); j.add(7, "days")) {
          let dtFrom = j;
          let dtTo = j.clone().add(6, "d");
          if (dtTo > moment()) {
            dtTo = moment();
          }
          const c = sentiment.filter((x) => x.SentimentDescription === legend[i] && moment(x.DatePosted).isBetween(dtFrom, dtTo));
          if (c) {
            arr[i].push(c.length);
          } else {
            arr[i].push(0);
          }
          setChartData(arr);
        }
      }
    } else if (days >= 90) {
      for (let i = dateFrom.clone(); i.isSameOrBefore(dateTo); i.add(1, "month")) {
        let dtFrom = i;
        let dtTo = i.clone().add(1, "M").subtract(1, "d");
        if (dtTo > moment()) {
          dtTo = moment();
        }
        _xAxis.push(`${dtFrom.format("D MMM YYYY")} - ${dtTo.format("D MMM YYYY")}`);
        setXAxis(_xAxis);
      }
      for (let i = 0; i < legend.length; i++) {
        arr.push([]);
        for (let j = dateFrom.clone(); j.isSameOrBefore(dateTo); j.add(1, "month")) {
          let dtFrom = j;
          let dtTo = j.clone().add(1, "M").subtract(1, "d");
          if (dtTo > moment()) {
            dtTo = moment();
          }
          const c = sentiment.filter((x) => x.SentimentDescription === legend[i] && moment(x.DatePosted).isBetween(dtFrom, dtTo));
          if (c) {
            arr[i].push(c.length);
          } else {
            arr[i].push(0);
          }
          setChartData(arr);
        }
      }
    } else {
      for (let i = dateFrom.clone(); i.isSameOrBefore(dateTo); i.add(1, "day")) {
        _xAxis.push(`${i.format("D MMM YYYY")}`);
        setXAxis(_xAxis);
      }
      for (let i = 0; i < legend.length; i++) {
        arr.push([]);
        for (let j = dateFrom.clone(); j.isSameOrBefore(dateTo); j.add(1, "day")) {
          const c = sentiment.filter((x) => x.SentimentDescription === legend[i] && moment(x.DatePosted).format("D MMM YYYY") === j.format("D MMM YYYY"));
          if (c) {
            arr[i].push(c.length);
          } else {
            arr[i].push(0);
          }
          setChartData(arr);
        }
      }
    }
  }, [dateFrom, dateTo, sentiment]);

  return (
    <div className="daily-insights">
      <div className="top">
        <p>Daily Insights</p>
      </div>
      <div className="body">
        {loading ? (
          <i className="fal fa-spinner fa-spin"></i>
        ) : (
          <ReactECharts
            option={{
              tooltip: {},
              legend: {
                data: legend,
                top: 10,
              },
              grid: {
                left: 30,
                right: 10,
                top: 45,
                bottom: 20,
              },
              xAxis: {
                data: xAxis,
              },
              yAxis: {
                minInterval: 1,
              },
              series: legend.map((x: string, i: number) => {
                return {
                  name: getClassification(x).name,
                  type: "line",
                  color: getClassification(x).color,
                  data: chartData[i],
                };
              }),
            }}
            onEvents={{
              click: onClick,
            }}
          />
        )}
      </div>
    </div>
  );
}

export default DailyInsights;
